/**
 * 应用级 Vuex 模块（单一对象）。
 *
 * 菜单、标签(keep-alive)、按钮权限、用户信息四类状态，全部平铺在这一个模块里，
 * 以模块 key `appStore` 注册，消费方统一走 state.appStore.* / 对应 getter。
 *
 * 用户信息：登录后由 cookie/接口一次性刷进 state.appStore.userInfo（见 flushUserInfoFromCookie），
 * 之后各处直接取 Vuex，不再依赖 global.js 里的可变单例。
 */
import { readUserInfoFromCookie } from '@/utils/userInfoCookie'

const appStore = {
  state: {
    // —— 菜单 ——
    collectMenus: [],
    currentTopIndex: 0,
    menuRouterLinks: [],
    menuRouterIndex: true,
    btnMenu: [],
    btnPermissions: [], // 按钮权限码列表
    // —— 用户信息（cookie/接口刷入，替代 global.js 单例）——
    userInfo: {},
    // —— 标签 / keep-alive ——
    visitedViews: [
      { path: '/index', name: 'index', query: {}, tagsName: 'homePage' }
    ],
    cachedViews: ['index'],
    fullPaths: []
  },
  getters: {
    visitedViews: state => state.visitedViews,
    cachedViews: state => state.cachedViews,
    // 按钮权限校验，供 getBtnPermission 使用
    hasBtnPermission: state => code => Array.isArray(state.btnPermissions) && state.btnPermissions.includes(code),
    // 用户信息
    userInfo: state => state.userInfo
  },
  mutations: {
    SET_COLLECT_MENUS(state, payload) {
      state.collectMenus = payload
      window.localStorage.setItem('commonMenus', JSON.stringify(payload))
    },
    SET_TOP_INDEX(state, payload) {
      state.currentTopIndex = payload
      window.localStorage.setItem('topIndex', JSON.stringify(payload))
    },
    SET_BTN_PERMISSIONS(state, codes) {
      state.btnPermissions = Array.isArray(codes) ? codes : []
      try {
        localStorage.setItem('btnPermissions', JSON.stringify(state.btnPermissions))
        // 通知依赖 btnPermissions 的模块（如 AI 助手初始化）缓存已就绪
        window.dispatchEvent(new Event('btnPermissionsReady'))
      } catch (error) {
        console.error('Failed to save btnPermissions:', error)
      }
    },
    SET_MENU_ROUTER_LINK(state, payload) {
      state.menuRouterLinks = payload
      const globalBtnMenu = payload.find(item => item.code === 'globalBtnPermission')
      const btnPermissions = globalBtnMenu?.children?.map(item => item.code) || []
      this.commit('SET_BTN_PERMISSIONS', btnPermissions)
      try {
        localStorage.setItem('menuRouterLinks', JSON.stringify(payload))
      } catch (error) {
        console.error('Failed to save menu data to localStorage:', error)
      }
    },
    SET_MENU_ROUTER_INDEX(state, payload) {
      state.menuRouterIndex = payload
    },
    // 用户信息：一次性刷入 / 增量合并
    SET_USER_INFO(state, payload) {
      state.userInfo = Object.assign({}, state.userInfo, payload || {})
    },
    addTags(state, payload) {
      let flag = state.visitedViews.some(
        item => ['smartQuery', 'historyQuery', 'pagesOutbound', 'pages-outbound'].includes(item.name) ? (item.path === payload.route.path) : (item.path === payload.route.path || item.name === payload.route.name)
      )
      // 子应用页签复用同一 path（如详情页按 query 区分单据），需同步最新 query，否则点回页签会跳到旧参数
      if (flag && payload.route.name === 'pages-outbound') {
        const existed = state.visitedViews.find(item => item.path === payload.route.path)
        if (existed) {
          existed.query = payload.route.query
        }
      }
      if (!flag) {
        state.visitedViews.push(
          Object.assign({}, {
            path: payload.route.path,
            name: payload.route.name,
            query: payload.route.query,
            // /guidedPack 命中 v2 白名单时，标签名固定为 packingV2（打包重构）
            tagsName: (payload.route.path === '/guidedPack' && window.__guidedPackIsV2)
              ? 'packingV2'
              : payload.curMenu.menuName || payload.curMenu.translation,
            curMenuInfo: payload.curMenu
          })
        )
      }
    },
    changeKeepAlive(state, param) {
      if (param.flag === 'add') {
        if (param.name && state.cachedViews.indexOf(param.name) === -1) {
          state.cachedViews.push(param.name)
        }
        if (param.fullPath && param.fullPath.indexOf('smartQuery') !== -1 && state.fullPaths.indexOf(param.fullPath) === -1) {
          state.fullPaths.push(param.fullPath)
        }
        if (param.fullPath && param.fullPath.indexOf('historyQuery') !== -1 && state.fullPaths.indexOf(param.fullPath) === -1) {
          state.fullPaths.push(param.fullPath)
        }
        if (param.fullPath && (param.fullPath.indexOf('pagesOutbound') !== -1 || param.fullPath.indexOf('pages-outbound') !== -1) && state.fullPaths.indexOf(param.fullPath) === -1) {
          state.fullPaths.push(param.fullPath)
        }
      } else if (param.flag === 'remove') {
        if (param.name && state.cachedViews.indexOf(param.name) !== -1) {
          state.cachedViews.splice(state.cachedViews.indexOf(param.name), 1)
        }
        if (param.fullPath && param.fullPath.indexOf('smartQuery') !== -1 && state.fullPaths.indexOf(param.fullPath) !== -1) {
          state.fullPaths.splice(state.fullPaths.indexOf(param.fullPath), 1)
        }
        if (param.fullPath && param.fullPath.indexOf('historyQuery') !== -1 && state.fullPaths.indexOf(param.fullPath) !== -1) {
          state.fullPaths.splice(state.fullPaths.indexOf(param.fullPath), 1)
        }
        if (param.fullPath && (param.fullPath.indexOf('pagesOutbound') !== -1 || param.fullPath.indexOf('pages-outbound') !== -1) && state.fullPaths.indexOf(param.fullPath) !== -1) {
          state.fullPaths.splice(state.fullPaths.indexOf(param.fullPath), 1)
        }
      } else if (param.flag === 'clear') {
        state.cachedViews = []
        state.fullPaths = []
      } else {
        console.log('flag is error,flag=' + param.flag)
      }
    },
    // micro-app 子应用卸载 / 切仓切租户时清空全部 keep-alive，防内存泄漏
    clearAllKeepAlive(state) {
      state.cachedViews = []
      state.fullPaths = []
    }
  },
  actions: {
    closeTags(context, payload) {
      for (const [key, item] of context.state.visitedViews.entries()) {
        if (item.path === payload.path) {
          return {
            delEl: context.state.visitedViews.splice(key, 1),
            index: key
          }
        }
      }
    },
    closeALLTags(context) {
      context.state.visitedViews = [
        { path: '/index', name: 'index', query: {}, tagsName: 'homePage' }
      ]
    },
    // 登录后单一入口：把 cookie 里的用户信息刷进 Vuex
    flushUserInfoFromCookie(context) {
      context.commit('SET_USER_INFO', readUserInfoFromCookie())
    }
  }
}

export default appStore
